import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import CloseButton from "../../components/Application/CloseButton";
import useCurrentUser from "../../hooks/useCurrentUser";
import { ArrowRightIcon } from "../../Icons";
import { caregiverHandbookPages } from "./caregiverEmployeeHandbookContent";

const DEFAULT_LOGO = "/login-logo.png";

const PLACEHOLDER_KEYS = [
  "COMPANY_NAME",
  "COMPANY_ADDRESS",
  "COMPANY_PHONE",
  "COMPANY_EMAIL",
  "ADMINISTRATOR",
  "CONTACT",
  "CONTACT_NAME",
  "HR_CONTACT",
  "AUTHORIZED_EXECUTIVE",
];

/**
 * @param {{ companyName?: string|null, tenant?: Record<string, unknown>|null, seniorStaffNurse?: string }} ctx
 */
export function buildCaregiverHandbookBranding(ctx) {
  const company = String(
    ctx.companyName || ctx.tenant?.companyName || "the Company",
  ).trim();
  const tenant = ctx.tenant || {};
  const addressParts = [
    tenant.address,
    tenant.street,
    [tenant.city, tenant.state, tenant.zipCode].filter(Boolean).join(", "),
  ].filter((p) => p && String(p).trim());
  const address = addressParts.join(", ") || "—";
  const phone = String(
    tenant.phone || tenant.officePhone || tenant.telephone || "—",
  ).trim();
  const email = String(tenant.email || tenant.companyEmail || "—").trim();
  const admin =
    String(ctx.seniorStaffNurse || tenant.seniorStaffNurse || "—").trim() ||
    "—";

  return {
    COMPANY_NAME: company,
    COMPANY_ADDRESS: address,
    COMPANY_PHONE: phone || "—",
    COMPANY_EMAIL: email || "—",
    ADMINISTRATOR: admin,
    CONTACT: admin !== "—" ? admin : "Supervisor / HR",
    CONTACT_NAME: admin !== "—" ? admin : "Administrator / HR",
    HR_CONTACT: admin !== "—" ? admin : "Payroll / HR",
    AUTHORIZED_EXECUTIVE:
      admin !== "—" ? admin : "Authorized Company Executive",
  };
}

export function applyHandbookPlaceholders(text, branding) {
  if (!text) return text;
  let out = String(text);
  PLACEHOLDER_KEYS.forEach((key) => {
    const value = branding[key] ?? "";
    out = out.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
  });
  out = out.replace(/\[COMPANY NAME\]/gi, branding.COMPANY_NAME || "");
  return out;
}

const boldCompany = (resolvedText, companyLabel) => {
  if (!resolvedText || !companyLabel) return resolvedText;
  const escaped = companyLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(resolvedText).split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, index) =>
    part.toLowerCase() === companyLabel.toLowerCase() ? (
      <strong key={`${part}-${index}`} className="font-semibold text-slate-900">
        {part}
      </strong>
    ) : (
      part
    ),
  );
};

const ReadOnlyField = ({ label, value, companyLabel, branding }) => {
  const resolved = applyHandbookPlaceholders(value || "—", branding);
  return (
    <div className="flex flex-col gap-1 border-b border-slate-100 py-2.5 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="w-44 shrink-0 text-[11px] font-semibold tracking-[0.08em] text-slate-500 uppercase">
        {label}
      </span>
      <span className="min-w-0 flex-1 text-[15px] text-slate-800">
        {boldCompany(resolved, companyLabel)}
      </span>
    </div>
  );
};

ReadOnlyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  companyLabel: PropTypes.string.isRequired,
  branding: PropTypes.object.isRequired,
};

const Paragraph = ({ text, branding, companyLabel }) => {
  const resolved = applyHandbookPlaceholders(text, branding);
  const isIntro =
    /:$/.test(resolved) &&
    resolved.length < 120 &&
    !/[.]/.test(resolved.slice(0, -1));

  if (isIntro) {
    return (
      <p className="mt-1 text-[15px] font-medium leading-relaxed text-slate-800">
        {boldCompany(resolved, companyLabel)}
      </p>
    );
  }

  return (
    <p className="text-[15px] leading-[1.7] text-slate-700">
      {boldCompany(resolved, companyLabel)}
    </p>
  );
};

Paragraph.propTypes = {
  text: PropTypes.string.isRequired,
  branding: PropTypes.object.isRequired,
  companyLabel: PropTypes.string.isRequired,
};

const SectionRenderer = ({ section, branding, companyLabel }) => {
  if (!section) return null;
  const heading = section.heading
    ? applyHandbookPlaceholders(section.heading, branding)
    : null;

  return (
    <section className="space-y-3">
      {heading ? (
        <h3 className="border-b border-slate-200 pb-2 text-base font-bold tracking-tight text-slate-900">
          {boldCompany(heading, companyLabel)}
        </h3>
      ) : null}

      {section.paragraphs?.map((paragraph, idx) => (
        <Paragraph
          key={`${heading || "p"}-${idx}-${String(paragraph).slice(0, 20)}`}
          text={paragraph}
          branding={branding}
          companyLabel={companyLabel}
        />
      ))}

      {(section.type === "meta" || section.type === "contactFields") &&
      section.fields?.length ? (
        <div className="mt-1 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-2">
          {section.fields.map((field) => (
            <ReadOnlyField
              key={field.label}
              label={field.label}
              value={field.value}
              branding={branding}
              companyLabel={companyLabel}
            />
          ))}
        </div>
      ) : null}

      {section.type === "toc" && section.items?.length ? (
        <ol className="m-0 grid list-none grid-cols-1 gap-x-8 gap-y-1.5 p-0 sm:grid-cols-2">
          {section.items.map((item, idx) => {
            const label = String(item).replace(/^\d+\.\s*/, "").trim();
            const num = idx + 1;
            return (
              <li
                key={`${num}-${label}`}
                className="flex items-baseline gap-2 text-[14px] leading-snug text-slate-700"
              >
                <span className="w-6 shrink-0 text-right text-[13px] font-semibold tabular-nums text-slate-500">
                  {num}.
                </span>
                <span className="min-w-0">{label}</span>
              </li>
            );
          })}
        </ol>
      ) : null}

      {section.type === "list" && section.items?.length ? (
        <ol className="list-decimal space-y-1.5 pl-5 marker:font-semibold marker:text-slate-500">
          {section.items.map((item, idx) => (
            <li
              key={`${heading || "li"}-${idx}-${String(item).slice(0, 28)}`}
              className="pl-1 text-[14.5px] leading-snug text-slate-700"
            >
              {boldCompany(
                applyHandbookPlaceholders(item, branding),
                companyLabel,
              )}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
};

SectionRenderer.propTypes = {
  section: PropTypes.object,
  branding: PropTypes.object.isRequired,
  companyLabel: PropTypes.string.isRequired,
};

/**
 * Classic read-only Caregiver Employee Handbook for CG / CNA.
 */
const CaregiverEmployeeHandbookReadOnly = ({ onClose }) => {
  const { officeLogo, companyName, tenant, seniorStaffNurse } = useCurrentUser();
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const branding = useMemo(
    () =>
      buildCaregiverHandbookBranding({
        companyName,
        tenant,
        seniorStaffNurse,
      }),
    [companyName, tenant, seniorStaffNurse],
  );

  const companyLabel = branding.COMPANY_NAME;
  const pages = caregiverHandbookPages;
  const totalPages = pages.length;
  const currentPage = pages[currentPageIndex] || pages[0];

  const getLogoUrl = () => {
    const tenantLogo = officeLogo || tenant?.officeLogo || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!tenantLogo || !String(tenantLogo).trim()) {
      return `${origin}${DEFAULT_LOGO}`;
    }
    if (String(tenantLogo).startsWith("http")) return tenantLogo;
    const normalizedPath = String(tenantLogo).startsWith("/")
      ? tenantLogo
      : `/${tenantLogo}`;
    return `${origin}${normalizedPath}`;
  };

  return (
    <>
      <CloseButton onClose={onClose} />
      <div className="mx-auto my-6 max-w-4xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:my-10">
        {/* Masthead — white theme */}
        <header className="border-b border-slate-200 bg-white px-6 py-5 sm:px-10">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-center sm:text-left">
            <img
              src={getLogoUrl()}
              alt=""
              className="h-14 w-auto max-w-[160px] object-contain"
              onError={(e) => {
                const origin =
                  typeof window !== "undefined" ? window.location.origin : "";
                e.target.src = `${origin}${DEFAULT_LOGO}`;
              }}
            />
            <div className="min-w-0 flex-1 sm:border-l sm:border-slate-200 sm:pl-5">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.75rem]">
                Employee Handbook
              </h1>
              <p className="mt-0.5 text-sm font-semibold text-slate-700">
                {companyLabel}
              </p>
            </div>
          </div>
        </header>

        {/* Reading surface */}
        <div className="bg-white px-5 py-6 sm:px-10 sm:py-8">
          <article className="min-h-[62vh]">
            <div className="space-y-8">
              {currentPage.sections.map((section, idx) => (
                <SectionRenderer
                  key={`${currentPage.pageNo}-${idx}-${section.heading || "sec"}`}
                  section={section}
                  branding={branding}
                  companyLabel={companyLabel}
                />
              ))}
            </div>
          </article>

          {/* Bottom navigation only */}
          <nav
            className="mt-10 flex items-center justify-between gap-3 border-t border-slate-200 pt-5"
            aria-label="Handbook pages"
          >
            <button
              type="button"
              onClick={() =>
                setCurrentPageIndex((prev) => Math.max(prev - 1, 0))
              }
              disabled={currentPageIndex === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span className="inline-flex rotate-180" aria-hidden="true">
                <ArrowRightIcon size={14} />
              </span>
              Previous
            </button>

            <p className="text-sm font-semibold text-slate-700">
              Page {currentPageIndex + 1} of {totalPages}
            </p>

            <button
              type="button"
              onClick={() =>
                setCurrentPageIndex((prev) =>
                  Math.min(prev + 1, totalPages - 1),
                )
              }
              disabled={currentPageIndex === totalPages - 1}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
              <span className="inline-flex" aria-hidden="true">
                <ArrowRightIcon size={14} />
              </span>
            </button>
          </nav>

          <footer className="mt-5 text-center text-xs tracking-wide text-slate-500">
            {companyLabel} · Caregiver Employee Handbook
          </footer>
        </div>
      </div>
    </>
  );
};

CaregiverEmployeeHandbookReadOnly.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CaregiverEmployeeHandbookReadOnly;
